import useAppStore from "@/context/store";
import { handlePinDetailPvReport } from "@/utils/hooks/usePinDetailPvReport";
import { useEffect } from "react";

// 避免重复监听
let refCount = 0;

// 记录上一次的 state，用于判断前进/后退方向
let previousState = null;

const onPopstate = (event) => {
  const path = window.location.pathname;
  const state = event.state || {};

  // 判断是前进还是后退
  const currentSeq = state.seq || 0;
  const previousSeq = previousState?.seq || 0;
  // 如果堆栈为空，说明是程序主动关闭，应该认为是后退而不是前进
  const { pinStack, popPinFromStack } = useAppStore.getState();
  const isForward = pinStack.length === 0 ? false : currentSeq > previousSeq;

  if (/^\/pins\/\d*$/.test(path)) {
    /**
     * 1.只有之前打开的是弹窗才需要设置pinStack
     * 2.弹窗的实现逻辑是url还是原url，只是as 变成了/pins/[pinId],所以判断state?.url如果为/pins/
     * [pinId]开头则前进之后的路径为详情页而不是弹窗，所以不需要设置
     **/
    if (
      !/^\/pins\/\[pinId\]/.test(state?.url) ||
      (state?.url.includes("/similar") &&
        !/^\/pins\/\d+\/similar/.test(state?.url))
    ) {
      const pinId = path.replace("/pins/", "");
      const { setPinStackToIndex, pinStack, pushPinToStack, clearPinStack } =
        useAppStore.getState();

      // 获取当前显示的 pinId（从堆栈最后一个元素获取，用于判断是否切换到了不同的详情页）
      const currentDisplayedPinId =
        pinStack.length > 0 ? pinStack[pinStack.length - 1]?.pinId : null;
      // 如果 state 中有 modalPinId，说明是堆栈导航
      if (state.modalPinId) {
        const modalPinId = String(state.modalPinId);
        // 检查这个 pinId 的弹窗是否还在堆栈中
        const existingIndex = pinStack.findIndex(
          (p) => String(p.pinId) === modalPinId
        );

        if (existingIndex >= 0) {
          // 存在，调整到该索引
          setPinStackToIndex(existingIndex);

          // 如果切换到了不同的详情页（详情页返回详情页），需要上报 PV
          const targetPin = pinStack[existingIndex];
          handlePinDetailPvReport({
            currentPinId: currentDisplayedPinId,
            targetPinId: modalPinId,
            targetPin,
          });
        } else {
          // 不存在（可能因为动态回收被移除了）
          if (isForward) {
            // ✅ 前进：重新添加到堆栈（当做新打开）
            pushPinToStack({
              pinId: modalPinId,
              allowEmpty: true, // 允许空数据，后续会加载完整数据
            });
          } else {
            // 后退：执行原有逻辑
            // 如果 modalPinId 不在堆栈中，说明可能被动态回收了
            // 这种情况下，调整到堆栈的最后一个元素，但不会真正切换详情页，所以不需要上报 PV
            if (pinStack.length > 0) {
              // pinStack 还有内容，调整到最后一个
              setPinStackToIndex(pinStack.length - 1);
            } else {
              // pinStack 为空，清空并回退
              clearPinStack();
              const timeout = setTimeout(() => {
                window.history.back();
                clearTimeout(timeout);
              }, 0);
            }
          }
        }
      } else {
        // 兼容旧逻辑或直接访问 pin 详情页
        // 检查是否已在堆栈中

        const existingIndex = pinStack.findIndex((p) => {
          const currentPinId = p.pinId || p.pin_id || p.pin?.pin_id;
          return currentPinId == pinId;
        });

        if (existingIndex >= 0) {
          // 已存在，调整到该索引（会移除后面的元素 = 出栈操作）
          setPinStackToIndex(existingIndex);
          // 如果切换到了不同的详情页（详情页返回详情页），需要上报 PV
          const targetPin = pinStack[existingIndex];
          handlePinDetailPvReport({
            currentPinId: currentDisplayedPinId,
            targetPinId: pinId,
            targetPin,
          });
        } else {
          // 不存在：什么都不做
          // 组件初始化时会自动调用 pushPinToStack（见 components/PinDetail/index.jsx:151）
          // 这里不需要手动 push，避免重复入栈
        }
      }

      // 兼容非全屏详情页
      useAppStore.setState({ currentPin: { pinId } });
    }
  } else {
    // 不是 pin 详情页路径，检查是否还有弹窗堆栈
    const { clearPinStack, pinStack, popPinFromStack } = useAppStore.getState();

    // 需要特殊处理的路由（需要判断是否从弹窗返回）
    const isSpecialRoute = path === "/space" || path === "/space/pins";

    // 判断是否从弹窗返回（通过 state 中的标识）
    const isFromModal = state?.isModal || state?.modalPinId;

    if (pinStack && pinStack.length > 0) {
      // 特殊路由且从弹窗返回：弹出堆栈（多个弹顶层，一个清空）
      if (isSpecialRoute && isFromModal) {
        pinStack.length > 1 ? popPinFromStack() : clearPinStack();
      } else {
        // 其他情况：统一清空堆栈
        clearPinStack();
      }
    }

    // 兼容非全屏详情页
    useAppStore.setState({ currentPin: null });
  }

  // 更新 previousState，用于下次判断方向
  previousState = state;
};

// 用于监听采集弹窗的浏览器前进后退事件
export default function usePinsPopstate() {
  useEffect(() => {
    // 避免在服务端渲染时监听
    if (typeof window === "undefined") {
      return;
    }

    if (refCount === 0) {
      // 初始化 previousState 为当前 state
      previousState = window.history.state || null;
      window.addEventListener("popstate", onPopstate);
    }
    refCount++;

    return () => {
      refCount--;
      if (refCount === 0) {
        window.removeEventListener("popstate", onPopstate);
      }
    };
  }, []);
}
